import React, { useState, useMemo } from 'react';
import ContentNode from './ContentNode';

// Configuration des types d'activités
const ACTIVITY_CONFIGS = {
  AUTOMATISME: {
    icon: "⚡",
    bg: "bg-emerald-100",
    bgSolid: "bg-emerald-500",
    border: "border-emerald-400",
    text: "text-emerald-800",
    textDark: "text-emerald-700",
    label: "Automatisme",
    labelPlural: "Automatismes"
  },
  RESOLUTION: {
    icon: "🧩",
    bg: "bg-rose-100",
    bgSolid: "bg-rose-500",
    border: "border-rose-400",
    text: "text-rose-800",
    textDark: "text-rose-700",
    label: "Résolution",
    labelPlural: "Résolutions de problèmes"
  },
  QFLASH: {
    icon: "💡",
    bg: "bg-amber-100",
    bgSolid: "bg-amber-500",
    border: "border-amber-400",
    text: "text-amber-800",
    textDark: "text-amber-700",
    label: "QFlash",
    labelPlural: "QFlash"
  },
};

// Composant badge compteur cliquable
const ActivityCountBadge = ({ type, count, onClick }) => {
  const config = ACTIVITY_CONFIGS[type] || ACTIVITY_CONFIGS.AUTOMATISME;

  return (
    <button
      onClick={onClick}
      className={`
        flex items-center gap-1.5 px-3 py-1.5 rounded-lg
        ${config.bg} border-2 ${config.border} ${config.text}
        text-xs font-bold hover:scale-105 transition-all duration-200
        hover:shadow-lg cursor-pointer shadow-sm
      `}
    >
      <span>{config.icon}</span>
      <span className="font-black">{count}</span>
    </button>
  );
};

// ─── Couleurs des thèmes ─────────────────────────────────────────────────
//
// Un DOMAINE garde sa couleur d'un cycle à l'autre (« Nombre et Calculs » et
// « Nombre, calcul et résolution de problèmes » sont le même rouge). Maths et
// français réutilisent la même gamme : un coordonnateur n'enseigne jamais les
// deux matières, aucune confusion possible.
//
// ⚠ La recherche est NORMALISÉE (accents et casse ignorés) : les libellés du
// référentiel varient d'un cycle à l'autre (« Espace et Géométrie » en 6e,
// « Espace et géométrie » en 5e rénové) et une comparaison brute renvoyait
// ces séquences au gris sans que rien ne l'explique. Même normalisation que
// le rattachement des thèmes à l'import.
// Classes ÉCRITES EN CLAIR : Tailwind ne conserve que ce qu'il voit
// littéralement dans les sources, un nom construit dynamiquement
// (`bg-${couleur}-500`) serait purgé du CSS final.
const ROUGE = { gradient: "from-red-400 to-red-600", bg: "bg-red-500", ring: "ring-red-400", text: "text-red-500" };
const BLEU = { gradient: "from-blue-400 to-blue-600", bg: "bg-blue-500", ring: "ring-blue-400", text: "text-blue-500" };
const EMERAUDE = { gradient: "from-emerald-400 to-emerald-600", bg: "bg-emerald-500", ring: "ring-emerald-400", text: "text-emerald-500" };
const VIOLET = { gradient: "from-purple-400 to-purple-600", bg: "bg-purple-500", ring: "ring-purple-400", text: "text-purple-500" };
const ZINC = { gradient: "from-zinc-400 to-zinc-600", bg: "bg-zinc-500", ring: "ring-zinc-400", text: "text-zinc-500" };
const ORANGE = { gradient: "from-orange-400 to-orange-600", bg: "bg-orange-500", ring: "ring-orange-400", text: "text-orange-500" };
const AMBRE = { gradient: "from-amber-400 to-amber-600", bg: "bg-amber-500", ring: "ring-amber-400", text: "text-amber-500" };
const FUCHSIA = { gradient: "from-fuchsia-400 to-fuchsia-600", bg: "bg-fuchsia-500", ring: "ring-fuchsia-400", text: "text-fuchsia-500" };
const GRIS = { gradient: "from-gray-400 to-gray-600", bg: "bg-gray-500", ring: "ring-gray-400", text: "text-gray-500" };

const normaliserTheme = (nom) => String(nom || '')
  .normalize('NFD')
  .replace(new RegExp('[' + String.fromCharCode(0x300) + '-' + String.fromCharCode(0x36f) + ']', 'g'), '')
  .toLowerCase()
  .replace(/\s+/g, ' ')
  .trim();

const COULEURS_THEMES = {
  // ── Mathématiques ──
  "Nombre et Calculs": ROUGE,
  "Nombres et calculs": ROUGE,                              // cycle 4 rénové
  "Nombre, calcul et résolution de problèmes": ROUGE,       // cycle 3
  "Espace et Géométrie": BLEU,
  "Grandeurs et mesures": EMERAUDE,
  "Organisation et gestion de données, fonctions": VIOLET,
  "Organisation et gestion de données et probabilités": VIOLET,
  "Algorithmique et programmation": ZINC,
  "Initiation à la pensée informatique": ZINC,              // cycle 3
  "La pensée informatique": ZINC,                           // cycle 4 rénové
  "La proportionnalité": ORANGE,
  "Proportionnalité, fonctions": ORANGE,                    // cycle 4 rénové
  // ── Français (mêmes teintes : un coordonnateur n'a qu'une matière) ──
  "Langage Oral": AMBRE,
  "Lecture": BLEU,
  "Lecture et compréhension de l'écrit": BLEU,              // cycle 4
  "Écriture": EMERAUDE,
  "Vocabulaire": VIOLET,
  "Grammaire et orthographe grammaticale": ROUGE,
  "Étude de la langue": ROUGE,                              // cycle 4 (fusion)
  "Culture littéraire et artistique": FUCHSIA,
};

// Index normalisé, construit une fois au chargement du module
const INDEX_COULEURS = Object.entries(COULEURS_THEMES).reduce((acc, [nom, couleur]) => {
  acc[normaliserTheme(nom)] = couleur;
  return acc;
}, {});

// Fonction pour obtenir la couleur du thème
export const getThemeColor = (themeNom) => INDEX_COULEURS[normaliserTheme(themeNom)] || GRIS;

// Fonction pour formater les dates
export const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
};

// Fonction pour calculer la durée en semaines
export const calculateDuration = (start, end) => {
  if (!start || !end) return '';
  const startDate = new Date(start);
  const endDate = new Date(end);
  const diffTime = Math.abs(endDate - startDate);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  const weeks = Math.ceil(diffDays / 7);
  return `${weeks} semaine${weeks > 1 ? 's' : ''}`;
};

// Fonction pour déterminer le statut d'une séquence temporaire par rapport à la date.
export const getSequenceStatus = (sequence) => {
  const now = new Date();
  const start = new Date(sequence.start);
  const end = new Date(sequence.end);

  if (now > end) return 'completed';
  if (now >= start && now <= end) return 'current';
  return 'locked';
};

// Composant principal pour une séquence
const SequenceNode = ({ sequence, index, activitesParalleles, onAddSavoir, onEdit, onDelete, onAddActivity, onViewActivities, onViewContenus }) => {
  const [expanded, setExpanded] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const status = getSequenceStatus(sequence);
  const isCurrent = status === 'current';
  const isCompleted = status === 'completed';
  // Toutes les séquences sont "actives" - même design et fonctionnalités pour toutes
  const isActive = true;
  const themeColors = getThemeColor(sequence.theme_for_sequence);

  // Fusionner savoir-faires et attendus avec un type pour chacun
  const savoirFaires = (sequence.savoir_faires_details || []).map(sf => ({ ...sf, _type: 'savoir-faire' }));
  const attendus = (sequence.attendus_details || []).map(att => ({ ...att, _type: 'attendu' }));
  const allContent = [...savoirFaires, ...attendus];

  // Alterne gauche/droite
  const leftItems = allContent.filter((_, i) => i % 2 === 0);
  const rightItems = allContent.filter((_, i) => i % 2 === 1);

  // Au-delà de 4 contenus, les satellites font plusieurs fois la hauteur de
  // la carte et flottent dans le vide (retour prof 2026-08-25, maquette
  // validée) → mode PILL : plus aucun satellite, la carte affiche une pill
  // « N savoirs et attendus » qui ouvre le panneau détail partagé
  // (PanneauContenus). ≤ 4 : affichage historique intact, le charme de
  // l'arbre est conservé.
  const modePill = allContent.length > 4;

  // Grouper les activités par type et compter
  const activityCounts = useMemo(() => {
    if (!activitesParalleles || activitesParalleles.length === 0) return {};

    const counts = {};
    activitesParalleles.forEach(act => {
      const type = act.type || 'AUTOMATISME';
      if (!counts[type]) {
        counts[type] = { count: 0, activities: [] };
      }
      counts[type].count++;
      counts[type].activities.push(act);
    });
    return counts;
  }, [activitesParalleles]);

  const hasActivities = Object.keys(activityCounts).length > 0;

  const handleActivityBadgeClick = (e, type) => {
    e.stopPropagation();
    // Appeler le callback parent avec le type et les activités
    if (onViewActivities) {
      onViewActivities(type, activityCounts[type]?.activities || [], sequence.title);
    }
  };

  return (
    <div className="relative">
      {/* Barre d'activités parallèles - compteurs groupés */}
      {isActive && (
        <div className="absolute -top-10 left-1/2 -translate-x-1/2 flex gap-2 z-20 flex-wrap justify-center items-center">
          {/* Badges compteurs par type */}
          {Object.entries(activityCounts).map(([type, data]) => (
            <ActivityCountBadge
              key={type}
              type={type}
              count={data.count}
              onClick={(e) => handleActivityBadgeClick(e, type)}
            />
          ))}
          {/* Bouton ajouter une activité en parallèle - visible seulement quand expanded */}
          {expanded && (
            <button
              onClick={(e) => { e.stopPropagation(); onAddActivity && onAddActivity(sequence); }}
              className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/80 border border-dashed border-blue-300 hover:bg-blue-50 hover:border-blue-400 transition-all duration-200 text-blue-500 hover:text-blue-600 shadow-sm"
              title="Ajouter une activité en parallèle"
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <span className="text-[10px] font-medium whitespace-nowrap">Ajouter activité</span>
            </button>
          )}
        </div>
      )}

      <div className="flex flex-col lg:flex-row items-center lg:items-start justify-center gap-4">
        {/* Contenus GAUCHE (savoir-faires + attendus) - Desktop uniquement */}
        <div className="hidden lg:flex flex-col gap-3 xl:gap-2 2xl:gap-2.5 3xl:gap-3 items-end w-72 xl:w-56 2xl:w-64 3xl:w-72 pt-4">
          {expanded && !modePill && leftItems.map((item, idx) => (
            <ContentNode key={item.id || idx} item={item} isActive={isActive} side="left" type={item._type} />
          ))}
          {/* Bouton ajouter à gauche si le prochain est pair */}
          {expanded && !modePill && isActive && allContent.length % 2 === 0 && (
            <div className="flex items-center gap-3 flex-row-reverse">
              <div className="relative">
                <div className="w-8 h-0.5 bg-slate-300" />
                <div className="absolute -left-1 top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-slate-400" />
              </div>
              <button
                onClick={(e) => { e.stopPropagation(); onAddSavoir && onAddSavoir(sequence); }}
                className="min-w-56 max-w-72 p-3 rounded-xl border-2 border-dashed border-blue-300 bg-white/50 hover:bg-blue-50 hover:border-blue-400 transition-all duration-300 flex items-center justify-center gap-2 text-blue-500 hover:text-blue-600"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                <span className="text-sm font-medium">Ajouter un savoir</span>
              </button>
            </div>
          )}
        </div>

        {/* SÉQUENCE principale */}
        <div
          className="relative z-10 cursor-pointer"
          onClick={() => setExpanded(!expanded)}
        >
          {/* Glow effect pour current */}
          {isCurrent && (
            <div className="absolute inset-0 -m-3 rounded-3xl bg-gradient-to-r from-orange-400/30 via-amber-400/30 to-orange-400/30 blur-xl animate-pulse" />
          )}

          {/* Points de connexion */}
          <div className={`
            absolute top-1/2 -left-4 w-3 h-3 rounded-full -translate-y-1/2 border-2 border-white
            ${isCompleted ? 'bg-emerald-500 shadow-lg shadow-emerald-500/50' : ''}
            ${isCurrent ? 'bg-orange-500 shadow-lg shadow-orange-500/50' : ''}
            ${!isActive ? 'bg-slate-300' : ''}
          `} />
          <div className={`
            absolute top-1/2 -right-4 w-3 h-3 rounded-full -translate-y-1/2 border-2 border-white
            ${isCompleted ? 'bg-emerald-500 shadow-lg shadow-emerald-500/50' : ''}
            ${isCurrent ? 'bg-orange-500 shadow-lg shadow-orange-500/50' : ''}
            ${!isActive ? 'bg-slate-300' : ''}
          `} />

          {/* Card principale */}
          <div className={`
            relative w-[420px] xl:w-[320px] 2xl:w-[370px] 3xl:w-[420px] p-6 xl:p-4 2xl:p-5 3xl:p-6 rounded-2xl transition-all duration-500
            backdrop-blur-xl
            ${isActive
              ? 'bg-white/95 border-2 border-white/70 shadow-2xl'
              : 'bg-white/40 border-2 border-white/30'
            }
            ${isActive ? `ring-2 ${themeColors.ring}` : ''}
          `}>
            {/* Numéro séquence */}
            <div className={`
              absolute -top-4 -left-4 w-12 h-12 xl:w-9 xl:h-9 2xl:w-10 2xl:h-10 3xl:w-12 3xl:h-12 rounded-xl flex items-center justify-center
              font-black text-lg xl:text-sm 2xl:text-base 3xl:text-lg border-2 border-white shadow-lg
              ${isActive ? `bg-gradient-to-br ${themeColors.gradient} text-white` : 'bg-slate-200 text-slate-600'}
            `}>
              S{index + 1}
            </div>

            {/* Status badge + Menu */}
            <div className="absolute -top-2 right-4 flex items-center gap-2">
       

              {/* Menu trois points */}
              <div className="relative">
                <button
                  onClick={(e) => { e.stopPropagation(); setMenuOpen(!menuOpen); }}
                  className={`
                    w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200
                    ${isActive
                      ? 'bg-slate-100 hover:bg-slate-200 text-slate-600'
                      : 'bg-slate-200/50 text-slate-400'
                    }
                  `}
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <circle cx="12" cy="6" r="2" />
                    <circle cx="12" cy="12" r="2" />
                    <circle cx="12" cy="18" r="2" />
                  </svg>
                </button>

                {/* Dropdown menu */}
                {menuOpen && (
                  <>
                    {/* Overlay pour fermer le menu */}
                    <div
                      className="fixed inset-0 z-40"
                      onClick={(e) => { e.stopPropagation(); setMenuOpen(false); }}
                    />
                    <div className="absolute right-0 top-full mt-1 w-40 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden z-50">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setMenuOpen(false);
                          onEdit && onEdit(sequence);
                        }}
                        className="w-full px-4 py-2.5 text-left text-sm font-medium text-yellow-600 hover:bg-yellow-50 hover:text-yellow-700 flex items-center gap-2 transition-colors"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        Modifier
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setMenuOpen(false);
                          onDelete && onDelete(sequence);
                        }}
                        className="w-full px-4 py-2.5 text-left text-sm font-medium text-orange-600 hover:bg-orange-50 flex items-center gap-2 transition-colors"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        Supprimer
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Contenu */}
            <div className="mt-4">
              {/* Thème */}
              <span className={`text-xs xl:text-[10px] 2xl:text-[11px] 3xl:text-xs font-medium uppercase tracking-wider ${isActive ? themeColors.text : 'text-slate-600'}`}>
                {sequence.theme_for_sequence}
              </span>

              {/* Titre */}
              <h3 className={`text-xl xl:text-sm 2xl:text-base 3xl:text-xl font-bold mt-1 mb-3 xl:mb-2 2xl:mb-2 3xl:mb-3 ${isActive ? 'text-slate-800' : 'text-slate-700'}`}>
                {sequence.title}
              </h3>

              {/* Estimation + Dates et durée */}
              <div className="mb-4 xl:mb-2 2xl:mb-3 3xl:mb-4">
                <p className={`text-[10px] italic mb-1 ${isActive ? 'text-slate-500' : 'text-slate-600'}`}>
                  Estimation
                </p>
                <div className="flex items-center gap-4 xl:gap-2 2xl:gap-3 3xl:gap-4 text-xs xl:text-[10px] 2xl:text-[11px] 3xl:text-xs">
                  <div className={`flex items-center gap-1.5 ${isActive ? 'text-blue-600' : 'text-slate-600'}`}>
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <span className="font-semibold">{formatDate(sequence.start)} → {formatDate(sequence.end)}</span>
                  </div>
                  <div className={`flex items-center gap-1.5 ${isActive ? 'text-purple-600' : 'text-slate-600'}`}>
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="font-semibold">{calculateDuration(sequence.start, sequence.end)}</span>
                  </div>
                </div>
              </div>

              {/* Stats savoir-faires et attendus - seulement si il y en a
                  (en mode pill, la pill porte le compteur) */}
              {isActive && allContent.length > 0 && !modePill && (
                <div className="mb-3">
                  <div className="flex justify-between text-[10px] text-slate-500 mb-1 font-medium">
                    <span>{savoirFaires.length} savoir-faire{savoirFaires.length > 1 ? 's' : ''}</span>
                    {attendus.length > 0 && (
                      <span>{attendus.length} attendu{attendus.length > 1 ? 's' : ''}</span>
                    )}
                  </div>

                </div>
              )}

              {/* Mode PILL (> 4 contenus) : le compteur cliquable ouvre le
                  panneau détail partagé — la frise ne se déforme jamais */}
              {isActive && modePill && (
                <div className="flex flex-col items-center gap-1 mb-1">
                  <button
                    onClick={(e) => { e.stopPropagation(); onViewContenus && onViewContenus(sequence); }}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-50 border-2 border-blue-300 text-blue-600 text-[13px] font-extrabold hover:bg-blue-100 hover:border-blue-400 hover:shadow-md transition-all"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
                    </svg>
                    {allContent.length} savoirs et attendus
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
                      <path d="M9 18l6-6-6-6" />
                    </svg>
                  </button>
                  <p className="text-[10px] text-slate-400">le détail s'ouvre dans un panneau</p>
                </div>
              )}

              {/* Message incitant à ajouter des savoir-faires si vide */}
              {isActive && allContent.length === 0 && (
                <button
                  onClick={(e) => { e.stopPropagation(); onAddSavoir && onAddSavoir(sequence); }}
                  className="w-full flex items-center justify-center gap-2 py-2 text-xs text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-all"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Cliquez pour ajouter des savoirs
                </button>
              )}

              {/* Bouton expand (satellites) - inutile en mode pill */}
              {allContent.length > 0 && !modePill && (
                <button className={`
                  w-full py-2 xl:py-1.5 2xl:py-1.5 3xl:py-2 rounded-lg text-sm xl:text-xs 2xl:text-xs 3xl:text-sm font-medium transition-all
                  ${isActive
                    ? 'bg-blue-50 text-blue-600 hover:bg-blue-100'
                    : 'bg-slate-100/80 text-slate-600'
                  }
                `}>
                  {expanded ? '▲ Masquer les savoirs-faires' : '▼ Voir les savoirs-faires'}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Contenus DROITE (savoir-faires + attendus) - Desktop uniquement */}
        <div className="hidden lg:flex flex-col gap-3 xl:gap-2 2xl:gap-2.5 3xl:gap-3 items-start w-72 xl:w-56 2xl:w-64 3xl:w-72 pt-4">
          {expanded && !modePill && rightItems.map((item, idx) => (
            <ContentNode key={item.id || idx} item={item} isActive={isActive} side="right" type={item._type} />
          ))}
          {/* Bouton ajouter à droite si le prochain est impair */}
          {expanded && !modePill && isActive && allContent.length % 2 === 1 && (
            <div className="flex items-center gap-3">
              <div className="relative">
                <div className="w-8 h-0.5 bg-slate-300" />
                <div className="absolute -right-1 top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-slate-400" />
              </div>
              <button
                onClick={(e) => { e.stopPropagation(); onAddSavoir && onAddSavoir(sequence); }}
                className="min-w-56 max-w-72 p-3 rounded-xl border-2 border-dashed border-blue-300 bg-white/50 hover:bg-blue-50 hover:border-blue-400 transition-all duration-300 flex items-center justify-center gap-2 text-blue-500 hover:text-blue-600"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                <span className="text-sm font-medium">Ajouter un savoir</span>
              </button>
            </div>
          )}
        </div>

        {/* Contenus MOBILE (tous les savoir-faires + attendus en dessous) -
            en mode pill, c'est le panneau qui porte le détail */}
        {expanded && !modePill && (
          <div className="lg:hidden flex flex-col gap-3 w-full max-w-sm px-4 mt-4">
            {allContent.map((item, idx) => (
              <ContentNode key={item.id || idx} item={item} isActive={isActive} side="bottom" type={item._type} />
            ))}
            {/* Bouton ajouter - Mobile */}
            {isActive && (
              <button
                onClick={(e) => { e.stopPropagation(); onAddSavoir && onAddSavoir(sequence); }}
                className="w-full p-3 rounded-xl border-2 border-dashed border-blue-300 bg-white/50 hover:bg-blue-50 hover:border-blue-400 transition-all duration-300 flex items-center justify-center gap-2 text-blue-500 hover:text-blue-600"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                <span className="text-sm font-medium">Ajouter un savoir</span>
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SequenceNode;
